<template>
  <BreadCrumb class="hidden sm:block" />
  <RoutinePage v-show="!isRestricted" :entry="entry" />
</template>

<script setup>
import { ref, onMounted } from 'vue';
import { useRoute } from 'vue-router';
import { useI18n } from 'vue-i18n';
import gql from 'graphql-tag';
import { stripLocale } from '@/utils/i18n';
import { enrichBlocksWithExerciseData } from '@/utils/algolia';
import RoutinePage, { RoutinePageFragment } from '@/components/routine/RoutinePage';
import { useEntryPage } from '@/composables/useEntryPage';
import { useBlackrollAuth } from '@/composables/useBlackrollAuth';
import { useLoyalty } from '@/composables/useLoyalty';
import { useUserData } from '@/composables/useUserData';
import { SeomaticFragment, EntryPageFragment } from '@/mixins/entryPage';
import BreadCrumb from '@/components/layout/BreadCrumb';
import { checkContentProtection } from '@/utils/contentProtection';

// Define component name
defineNuxtComponent({
  name: 'RoutinePageController',
});

// Component state
const entry = ref(null);
const seomatic = ref(null);
const isRestricted = ref(false);

// Setup composables
const { $craftRequest, $algoliaWorkouts } = useNuxtApp();
const route = useRoute();
const { locale } = useI18n();
const auth = useBlackrollAuth();
const loyalty = useLoyalty();
const userData = useUserData();

// Define GraphQL query
const RoutinePageQuery = gql`
  query RoutinePageQuery($site: String!, $uri: String!) {
    entry(section: "routine", site: [$site], uri: [$uri]) {
      title
      ...EntryPageFragment
      ...RoutinePageFragment
    }

    seomatic(site: $site, uri: $uri, asArray: true) {
      ...SeomaticFragment
    }
  }

  ${EntryPageFragment}
  ${RoutinePageFragment}
  ${SeomaticFragment}
`;

// Fetch data
const uri = stripLocale(route.path, locale.value);
const variables = { site: locale.value, uri };

const { data, error } = await useAsyncData(`routinePage-${uri}-${locale.value}`, async (nuxtApp) => {
  const { entry: entryData, seomatic: seomaticData } = await $craftRequest(RoutinePageQuery, variables);

  if (entryData === null) {
    return { entry: null, seomatic: null };
  }

  // Check content protection before rendering (throws if access denied)
  checkContentProtection(entryData, nuxtApp, auth, loyalty, userData);

  if (entryData.pimWorkout) {
    // save default block in new variable
    const additionalBlocks = [...entryData.contentBlocks];
    // get algolia data for workout
    const workout = await $algoliaWorkouts.fetchFegWorkoutById(entryData.pimWorkout);

    // after fetching workout from algolia, we need to map it to the correct format
    // for the exercises block component
    const blocks = workout.exercises.map((exercise) => {
      return {
        typeHandle: 'exercise',
        exerciseObjectId: exercise.workoutExerciseId,
        heading: exercise.blockHeading,
        description: exercise.blockDescription,
        title: exercise.titleOverwrite ?? exercise.title,
        shortDescription: exercise.shortDescriptionOverwrite ?? exercise.shortDescription,
        longDescription: exercise.descriptionOverwrite ?? exercise.longDescription,
        practiceLabelKey: exercise.practice?.length > 0 ? exercise.practice : null,
        practiceValue: exercise.repetitions
          ? `${exercise.repetitions}`
          : exercise.sets && exercise.sets && exercise.seconds
            ? `${exercise.sets} / ${exercise.seconds}`
            : exercise.seconds > 0
              ? `${exercise.seconds}`
              : null,
      };
    });

    // merge default blocks with exercise blocks from algolia
    additionalBlocks.forEach((block) => {
      if (block.position) {
        // assume that we get position counting from 1 to n
        // reduce pos by 1, so we can add it to the front of the array :)
        const position = Math.max(0, block.position - 1);
        blocks.splice(position, 0, block);
      } else {
        blocks.push(block);
      }
    });

    // assign the blocks to the entry
    entryData.contentBlocks = blocks;
  }

  // enrich blocks with algolia exercise data
  try {
    await enrichBlocksWithExerciseData(entryData.contentBlocks);
  } catch (error) {
    console.error('Error enriching blocks with exercise data:', error);
  }

  return { entry: entryData, seomatic: seomaticData };
});

if (error.value) {
  throw error.value;
}

// Set page data from async data result
if (data.value) {
  entry.value = data.value.entry;
  seomatic.value = data.value.seomatic;
}

const { gtmPushPageMeta } = useEntryPage(entry.value, seomatic.value);

// Set up page metadata
onMounted(() => {
  gtmPushPageMeta({ content_category: 'exercises' });
});
</script>
